import DesignTokens from '@tableau/design-tokens';
import { PencilBaseIcon } from '@tableau/icons/PencilBaseIcon';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';

import { dialogBodyStyle } from '~src/catalog/styles/ExternalContentActionsStyles';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { ISanitizer } from '~src/commonInfra/utils/Sanitize';
import { FlowDescriptionField } from '~src/flow/components/content/FlowEditableTextArea';
import { useSafeExternalLinks } from '~src/leaving/UseSafeExternalLinks';
import { applyColor } from '~src/style/ColorService';

import { Messages } from '../../codegen/Localize';
import { TextileHelper } from '../../spacesPlatform/utils/TextileHelper';
import { cssClass, flexColumnStyle, flexRowStyle } from '../../style';
import { ChangeFlowDescriptionAction } from '../actions/ChangeFlowDescriptionAction';
import { FlowDetailInfo } from '../models/FlowInfo';

export const editFlowDescriptionButton = 'EDIT-FLOW-DESCRIPTION-BUTTON';
const cssPrefix = 'FlowDescription';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle);

const headerClass = cssClass(cssPrefix, 'header', flexRowStyle, {
  fontWeight: 'normal',
  fontSize: CommonStyles.Sizes.HeaderFontSize
});

const descriptionContainer = cssClass(cssPrefix, 'description', {
  paddingLeft: 2
});

const noDescription = cssClass(cssPrefix, 'noDescription', {
  paddingTop: CommonStyles.Sizes.DefaultSpace,
  paddingBottom: CommonStyles.Sizes.DefaultSpace
});

const editButton = cssClass(cssPrefix, 'editButton', {
  marginLeft: CommonStyles.Sizes.DefaultSpace,
  minWidth: CommonStyles.Sizes.NormalIconSize,
  height: CommonStyles.Sizes.NormalIconSize,
});

export type FlowDescriptionProps = {
  readonly editDescriptionAllowed: boolean;
  readonly flowDetailInfo: FlowDetailInfo;
  readonly refreshData: () => void;
  readonly sanitizer: ISanitizer;
};

export class FlowDescription extends React.PureComponent<FlowDescriptionProps> {
  private toggleEditing = () => {

    const handleSubmit = (description: string) => {
      ChangeFlowDescriptionAction.execute(this.props.flowDetailInfo, description)
        .then(() => {
          this.props.refreshData();
          modal.close();
        });
    };

    const dialogTitle = Messages.editDescription2();

    const modal = ReactModalService.open({
      title: dialogTitle,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: false,
      dismissDialogOnCloseAction: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: <FlowDescriptionField
        description={this.props.flowDetailInfo.description}
        dismissDialog={() => modal.dismiss()}
        doAction={handleSubmit}
        textAreaLabel={dialogTitle}
      />,
      dialogBodyStyle: dialogBodyStyle,
      dialogWidth: TabDialogWidth.width500px
    });

    return Promise.resolve(modal.result)
      .then(() => this.props.refreshData())
      .catch(reason => {
        if (reason === 'cancel' || reason === 'dismissDialog')
          Promise.resolve(reason);
        else
          Promise.reject(reason);
      });
  };

  private EditButton() {
    return (
      <ButtonWidget
        className={editButton}
        buttonType={ButtonType.Icon}
        getContent={this.EditButtonIcon}
        onClick={this.toggleEditing}
        testId={editFlowDescriptionButton}
        title={Messages.editDescription2()}
      />
    );
  }

  private EditButtonIcon() {
    return (
      <PencilBaseIcon
        alt=''
        color={applyColor(DesignTokens.color.icon.default.default, '#666')}
        size={18}
      />
    );
  }

  public render() {
    const santizedDescriptionHtml = TextileHelper.getSanitizedHtmlFromTextile(
      this.props.flowDetailInfo.description,
      this.props.sanitizer
    );

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div className={rootClass}>
        <div className={headerClass}>
          <div>{Messages.description()}</div>
          {this.props.editDescriptionAllowed ? this.EditButton() : null}
        </div>
        <div className={descriptionContainer}>
          {this.props.flowDetailInfo.description
            ? <DescriptionWithSafeExternalLinks sanitizedHtml={santizedDescriptionHtml} />
            : <div className={noDescription}>{Messages.flowOverviewNoDescription()}</div>}
        </div>
      </div>
    );
  }
}

export function DescriptionWithSafeExternalLinks({ sanitizedHtml }: { sanitizedHtml: string }) {
  const { ref } = useSafeExternalLinks(sanitizedHtml);

  // htmlDescription is already sanitized so suppress the error
  // eslint-disable-next-line jam3/no-sanitizer-with-danger
  return <div ref={ref} className={'tb-textile'} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
}
